/**
 * Real API client for `/api/chouette` and the shared memoneo auth/jobs/audio
 * endpoints it rides on. Same auth convention as memoneo's frontend/src/lib/
 * api.ts lines 1-20 (Bearer JWT in localStorage under the "jwt" key) —
 * `authHeaders()` below mirrors it exactly so a shared backend session just
 * works.
 *
 * This module is never imported directly by pages — src/lib/dataSource.ts
 * picks between this and src/lib/mock.ts (same shared interface) and pages
 * import from there.
 */
import type {
  AiJob,
  ApiCallResult,
  AppearanceInput,
  Catalog,
  Child,
  ChildCreateInput,
  ChildInput,
  ChildSheetsOut,
  ChouetteEventKind,
  CreateStoryBody,
  CreateStoryResponse,
  ListenEventKind,
  Me,
  PairingConsumeResponse,
  PairingCreateResponse,
  StoryDetail,
} from "./types";

export const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";
export const AUTH_BASE = import.meta.env.VITE_AUTH_BASE ?? "/auth";

export function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("jwt");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Decode the `sub` claim (user id) from a JWT without verifying the
 *  signature — same convention as memoneo's frontend/src/lib/api.ts
 *  (`decodeJwtSub`/`currentUserId`). Used only to fill `target_user_id` when
 *  minting a pairing code for "this same account, on another device", never
 *  for authorization. */
export function decodeJwtSub(token: string): string | null {
  try {
    const payload = JSON.parse(atob(token.split(".")[1])) as { sub?: unknown };
    return typeof payload.sub === "string" ? payload.sub : null;
  } catch {
    return null;
  }
}

/** User id of the account currently signed in on this device (from the
 *  local `jwt`), or null when signed out. */
export function currentUserId(): string | null {
  const token = localStorage.getItem("jwt");
  return token ? decodeJwtSub(token) : null;
}

/** Thrown on a 402 `insufficient_credits` response — same shape as memoneo's
 *  app.services.credits.InsufficientCredits handler. Pages catch this to
 *  show the quota wall instead of a generic error. */
export class InsufficientCreditsError extends Error {
  constructor(
    public required: number,
    public balance: number,
  ) {
    super("insufficient_credits");
    this.name = "InsufficientCreditsError";
  }
}

/** Erreur d'API qui garde le statut HTTP : le message reprend le `detail`
 *  du serveur quand il y en a un, donc on ne peut pas reconnaître un 429 à
 *  son seul texte (la vérification du code renvoie « Too many attempts… »). */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function throwApiError(res: Response): Promise<never> {
  const body: unknown = await res.json().catch(() => ({}));
  const record = (body ?? {}) as { detail?: unknown; required?: unknown; balance?: unknown };
  if (res.status === 402 && record.detail === "insufficient_credits") {
    throw new InsufficientCreditsError(Number(record.required ?? 0), Number(record.balance ?? 0));
  }
  const message = typeof record.detail === "string" ? record.detail : `HTTP ${res.status}`;
  throw new ApiError(message, res.status);
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...authHeaders(),
      ...init.headers,
    },
  });
  if (!res.ok) return throwApiError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export function requestMagicLink(email: string): Promise<{ ok: boolean }> {
  return fetch(`${AUTH_BASE}/magic-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, origin: window.location.origin }),
  }).then(async (res) => {
    if (!res.ok) return throwApiError(res);
    return (await res.json().catch(() => ({ ok: true }))) as { ok: boolean };
  });
}

export const devLoginUrl = `${AUTH_BASE}/dev-login`;

/**
 * Device pairing — `POST /api/pairing/create`, from an already-logged-in
 * browser (Bearer required; `apiFetch` attaches it). Field names mirror
 * memoneo's `PairingCreateRequest`/`PairingCreateResponse` exactly:
 * `target_user_id` in, `{code, expires_at, qr_payload}` out. These live
 * under the shared `/api/pairing/*` prefix, not `/chouette` — same
 * `API_BASE`, since it already carries the `/api` segment. */
export function pairingCreate(targetUserId: string): Promise<PairingCreateResponse> {
  return apiFetch<PairingCreateResponse>("/pairing/create", {
    method: "POST",
    body: JSON.stringify({ target_user_id: targetUserId }),
  });
}

/** `POST /api/pairing/consume` — public, no auth header needed (and none
 *  sent, since this runs before the device has a JWT of its own). */
export function pairingConsume(code: string, deviceLabel?: string): Promise<PairingConsumeResponse> {
  return apiFetch<PairingConsumeResponse>("/pairing/consume", {
    method: "POST",
    body: JSON.stringify({ code, device_label: deviceLabel }),
  });
}

export function getCatalog(childId?: string, opts: { adminView?: boolean } = {}): Promise<Catalog> {
  const qs = new URLSearchParams();
  if (childId) qs.set("child_id", childId);
  // Admin-only on the backend (ignored for anyone else): adds scheduled
  // free stories and retired packs. See `lib/adminView.ts`.
  if (opts.adminView) qs.set("include_scheduled", "1");
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiFetch<Catalog>(`/chouette/catalog${suffix}`);
}

export async function getStory(id: string): Promise<StoryDetail> {
  const story = await apiFetch<StoryDetail>(`/chouette/stories/${id}`);
  // Normalize at the boundary: every caller (player, offline download,
  // the SW cache key) then works with one URL shape. See absoluteAudioUrl.
  return story.audio_url ? { ...story, audio_url: absoluteAudioUrl(story.audio_url) } : story;
}

export function getMyStories(childId?: string): Promise<import("./types").StoryCard[]> {
  const qs = childId ? `?child_id=${encodeURIComponent(childId)}` : "";
  return apiFetch(`/chouette/stories/mine${qs}`);
}

export function createStory(body: CreateStoryBody): Promise<CreateStoryResponse> {
  return apiFetch<CreateStoryResponse>("/chouette/stories", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function setFavorite(storyId: string, favorite: boolean): Promise<void> {
  return apiFetch(`/chouette/stories/${storyId}/favorite`, { method: favorite ? "POST" : "DELETE" });
}

export function deleteStory(storyId: string): Promise<void> {
  return apiFetch(`/chouette/stories/${storyId}`, { method: "DELETE" });
}

export function listChildren(): Promise<Child[]> {
  return apiFetch("/chouette/children");
}

export function createChild(input: ChildCreateInput): Promise<Child> {
  return apiFetch("/chouette/children", { method: "POST", body: JSON.stringify(input) });
}

export function patchChild(id: string, input: Partial<ChildInput>): Promise<Child> {
  return apiFetch(`/chouette/children/${id}`, { method: "PATCH", body: JSON.stringify(input) });
}

export function deleteChild(id: string): Promise<void> {
  return apiFetch(`/chouette/children/${id}`, { method: "DELETE" });
}

/**
 * Child appearance and reference sheets (docs/plans/2026-09-27-chouette-
 * planche-enfant.md, lot 2). Guided choices only, no photo upload — see
 * `AppearanceInput` in types.ts.
 */
export function putChildAppearance(childId: string, body: AppearanceInput): Promise<Child> {
  return apiFetch(`/chouette/children/${childId}/appearance`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

/** Starts one batch of 3 candidate sheets (202). 409 `appearance_missing`,
 *  409 `sheets_already_generating`, 429 `sheet_batches_exhausted` — thrown
 *  as a plain `Error` with that `detail` as its message, same convention
 *  as every other `apiFetch` call. */
export function startChildSheets(childId: string): Promise<ChildSheetsOut> {
  return apiFetch(`/chouette/children/${childId}/sheets`, { method: "POST" });
}

export function getChildSheets(childId: string): Promise<ChildSheetsOut> {
  return apiFetch(`/chouette/children/${childId}/sheets`);
}

export function chooseChildSheet(childId: string, sheetId: string): Promise<Child> {
  return apiFetch(`/chouette/children/${childId}/sheets/${sheetId}/choose`, { method: "POST" });
}

/** The sheet's WebP preview lives behind the Bearer JWT (private data — see
 *  the endpoint's own docstring), so a plain `<img src>` would get a 401.
 *  Fetched as a blob instead; `src/lib/childSheetImage.ts` turns it into an
 *  object URL. */
export async function fetchChildSheetImageBlob(childId: string, sheetId: string): Promise<Blob> {
  const res = await fetch(`${API_BASE}/chouette/children/${childId}/sheets/${sheetId}/image`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) return throwApiError(res);
  return res.blob();
}

/**
 * A 256x256 WebP already centred on the child's face — always answers 200
 * for a visible sheet (computed lazily server-side for older sheets), so
 * every small thumbnail (Settings child card, the appearance screen's
 * "Propositions précédentes" row) can show a plain square image with no CSS
 * offset, instead of the fixed `frontPoseCrop` guess over the full preview.
 * Same private-endpoint convention as `fetchChildSheetImageBlob`. Callers go
 * through `src/lib/childSheetThumbnail.ts`, which falls back to the full
 * preview and its CSS crop when this fails (404 on an older backend not yet
 * deployed, or a network error). */
export async function fetchChildSheetPortraitBlob(childId: string, sheetId: string): Promise<Blob> {
  const res = await fetch(`${API_BASE}/chouette/children/${childId}/sheets/${sheetId}/portrait`, {
    headers: { ...authHeaders() },
  });
  if (!res.ok) return throwApiError(res);
  return res.blob();
}

export function getMe(): Promise<Me> {
  return apiFetch("/chouette/me");
}

export function setKidPin(pin: string): Promise<void> {
  return apiFetch("/chouette/kid-pin", { method: "POST", body: JSON.stringify({ pin }) });
}

export function verifyKidPin(pin: string): Promise<{ ok: boolean }> {
  return apiFetch("/chouette/kid-pin/verify", { method: "POST", body: JSON.stringify({ pin }) });
}

/** Code parent oublié : le backend envoie un lien d'effacement par e-mail au
 *  propriétaire du compte (204 même si l'envoi échoue, pour ne rien révéler ;
 *  429 si trop de demandes). Authentifié : seul l'appareil connecté peut le
 *  demander. */
export function requestPinReset(): Promise<void> {
  return apiFetch("/chouette/kid-pin/reset-request", { method: "POST" });
}

/** Efface le code parent à partir du jeton reçu par e-mail. Appel volontairement
 *  SANS `authHeaders()` : le lien s'ouvre souvent dans un navigateur (ou une
 *  PWA) où le parent n'est pas connecté, et le jeton suffit à prouver la
 *  possession de la boîte mail. Même schéma que `requestMagicLink`. */
export function resetPin(token: string): Promise<void> {
  return fetch(`${API_BASE}/chouette/kid-pin/reset`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  }).then(async (res) => {
    if (!res.ok) return throwApiError(res);
  });
}

/** Vrai pour l'erreur levée par `throwApiError` sur un 429 (trop d'essais). */
export function isRateLimitedError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 429;
}

export function unlockPack(id: string): Promise<void> {
  return apiFetch(`/chouette/packs/${id}/unlock`, { method: "POST" });
}

export function getAiJob(jobId: string): Promise<AiJob> {
  return apiFetch(`/ai-jobs/${jobId}`);
}

export function audioUrl(assetId: string): string {
  return `${API_BASE}/audio/${assetId}`;
}

/** Rewrite the backend's `audio_url` (`/api/audio/<asset id>`, relative) onto
 *  `API_BASE`.
 *
 *  In dev both are the same origin (Vite proxies `/api`), so the relative
 *  path works and this is a no-op. In production the app is served from
 *  chouette.memoneo.app while the API lives on api.memoneo.app — and the
 *  SPA catch-all (`public/_redirects`) answers ANY unmatched path with
 *  index.html and a 200, so the `<audio>` element was handed HTML instead
 *  of an MP3: duration 0:00 and a play button that did nothing, with no
 *  network error to show for it (2026-09-19, first prod test). */
export function absoluteAudioUrl(url: string): string {
  if (!url.startsWith("/api/")) return url;   // data: URI, already absolute…
  return `${API_BASE}${url.slice("/api".length)}`;
}

/** `POST /chouette/stories/{id}/listen` — called from src/lib/listenQueue.ts,
 *  never directly from a page. Bypasses `apiFetch` on purpose: that helper
 *  throws on any non-2xx status, which would collapse "story no longer
 *  readable" (404, drop the queued entry) and "network error" (keep it, try
 *  again later) into the same catch block. Returning the raw status lets the
 *  queue tell them apart. */
export function postListen(storyId: string, event: ListenEventKind, at: string): Promise<ApiCallResult> {
  return fetch(`${API_BASE}/chouette/stories/${storyId}/listen`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ event, at }),
  }).then((res) => ({ status: res.status }));
}

/** `POST /chouette/events` — auth optional (`authHeaders()` already returns
 *  `{}` signed out, matching the contract). Same raw-status shortcut as
 *  `postListen`; nothing reads the response body either way (204, empty). */
export function postEvent(kind: ChouetteEventKind, meta: Record<string, unknown>): Promise<ApiCallResult> {
  return fetch(`${API_BASE}/chouette/events`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...authHeaders() },
    body: JSON.stringify({ kind, meta }),
  }).then((res) => ({ status: res.status }));
}
